import { useState, useEffect } from 'react';
import { dossiersApi, usersApi } from '@/api';
import Button from '@/components/ui/Button';

const ROLE_LABELS = {
  admin: 'Admin',
  expert_senior: 'Expert Senior',
  expert_junior: 'Expert Junior',
};

export default function AssignExpertModal({
  isOpen,
  dossierId,
  currentExpertId,
  onClose,
  onAssigned,
}) {
  const [experts, setExperts] = useState([]);
  const [selected, setSelected] = useState(currentExpertId || '');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    usersApi
      .listAssignable()
      .then((list) => setExperts(list))
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    setSelected(currentExpertId || '');
  }, [currentExpertId, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await dossiersApi.changeExpert(dossierId, selected || null);
      onAssigned?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Erreur lors de l'assignation");
    } finally {
      setSubmitting(false);
    }
  };

  const options = experts.map((u) => ({
    value: u.id,
    label: `${u.prenom} ${u.nom} (${ROLE_LABELS[u.role] || u.role})`,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">
          Assigner un expert
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {loading ? (
            <p className="text-sm text-slate-500">Chargement des experts...</p>
          ) : (
            <div className="space-y-1">
              <label
                htmlFor="expert_id"
                className="block text-sm font-medium text-slate-700"
              >
                Expert assigne
              </label>
              <select
                id="expert_id"
                name="expert_id"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
              >
                <option value="">— Non assigne —</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || loading}
            >
              {submitting ? 'Assignation...' : 'Assigner'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
